import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { useNavigate } from 'react-router-dom';
import {
  FiUpload,
  FiPlus,
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiCalendar,
  FiX,
  FiPaperclip,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import AddApplicant from './AddApplicant';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import {
  APPLICANTS_QUERY,
  REMOVE_APPLICANT,
  RECRUITMENT_STAGES,
  applicantInitials,
  priorityLabel,
  stageTitle,
  mapApplicant,
} from '../data/recruitmentApi';
import '../styles/recruitmentShared.css';
import '../styles/Applicants.css';

const PAGE_SIZE = 10;
const STAGE_TABS = ['all', ...RECRUITMENT_STAGES.map((s) => s.id)];

export default function Applicants() {
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [jobPosition, setJobPosition] = useState('');
  const [branch, setBranch] = useState('');
  const [recruiter, setRecruiter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const overlay = useAdminListOverlay([], { allowedTabs: STAGE_TABS });
  const stageTab = overlay.tab || 'all';
  const showAdd = overlay.showAdd;
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await graphqlRequest(APPLICANTS_QUERY);
      const rows = Array.isArray(data?.recruitmentApplicants)
        ? data.recruitmentApplicants
        : [];
      setApplicants(rows.map(mapApplicant));
    } catch (err) {
      console.error(err);
      setApplicants([]);
      setError(err?.message || 'Failed to load applicants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchApplicants();
  }, [fetchApplicants]);

  const jobOptions = useMemo(
    () => [...new Set(applicants.map((a) => a.role).filter(Boolean))].sort(),
    [applicants],
  );
  const branchOptions = useMemo(
    () => [...new Set(applicants.map((a) => a.branch).filter(Boolean))].sort(),
    [applicants],
  );
  const recruiterOptions = useMemo(
    () =>
      [...new Set(applicants.map((a) => a.recruiter).filter(Boolean))].sort(),
    [applicants],
  );

  const stageCounts = useMemo(() => {
    const counts = { all: applicants.length };
    RECRUITMENT_STAGES.forEach((s) => {
      counts[s.id] = applicants.filter((a) => a.stage === s.id).length;
    });
    return counts;
  }, [applicants]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = applicants.filter((a) => {
      if (stageTab !== 'all' && a.stage !== stageTab) return false;
      if (jobPosition && a.role !== jobPosition) return false;
      if (branch && a.branch !== branch) return false;
      if (recruiter && a.recruiter !== recruiter) return false;
      if (stageFilter && a.stage !== stageFilter) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.role.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.phone.toLowerCase().includes(q) ||
        a.appId.toLowerCase().includes(q)
      );
    });

    if (sortBy === 'name') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'priority') {
      const rank = { high: 0, normal: 1, low: 2 };
      list = [...list].sort(
        (a, b) => (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9),
      );
    }

    return list;
  }, [applicants, search, jobPosition, branch, recruiter, stageFilter, stageTab, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const goRecruitment = () => {
    navigate({ pathname: '/', search: '?page=recruitment' }, { replace: true });
  };

  const handleDelete = async (applicantId) => {
    if (!await appConfirmMessage('Delete this applicant?')) return;
    try {
      await graphqlRequest(REMOVE_APPLICANT, { id: applicantId });
      toast.success('Applicant deleted.');
      await fetchApplicants();
    } catch (err) {
      toast.error(err?.message || 'Could not delete applicant.');
    }
  };

  return (
    <div className="rm-shared ra-page">
      <nav className="rm-crumb" aria-label="Breadcrumb">
        <button type="button" className="rm-crumb-back" onClick={goRecruitment}>
          Recruitment
        </button>
        <span className="rm-crumb-sep">/</span>
        <span>Applicants</span>
      </nav>

      <header className="rm-page-header">
        <div>
          <h1 className="rm-page-title">Applicants</h1>
          <p className="rm-page-subtitle">
            {applicants.length} total applicants across all stages.
          </p>
        </div>
        <div className="rm-actions">
          <button type="button" className="rm-btn rm-btn--ghost">
            <FiUpload />
            Import Resume
          </button>
          <button
            type="button"
            className="rm-btn rm-btn--primary"
            onClick={() => overlay.openAdd()}
          >
            <FiPlus />
            Add Applicant
          </button>
        </div>
      </header>

      {error ? (
        <div className="rm-error-banner">
          <span>{error}</span>
          <button type="button" onClick={() => void fetchApplicants()}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="rm-filters">
        <label className="rm-search">
          <FiSearch />
          <input
            type="search"
            placeholder="Search applicants, positions, email, phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>

        <div className="rm-select-wrap">
          <select
            value={jobPosition}
            onChange={(e) => {
              setJobPosition(e.target.value);
              setPage(1);
            }}
            aria-label="Job Position"
          >
            <option value="">Job Position</option>
            {jobOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <FiChevronDown />
        </div>

        <div className="rm-select-wrap">
          <select
            value={branch}
            onChange={(e) => {
              setBranch(e.target.value);
              setPage(1);
            }}
            aria-label="Branch"
          >
            <option value="">Branch</option>
            {branchOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <FiChevronDown />
        </div>

        <div className="rm-select-wrap">
          <select
            value={recruiter}
            onChange={(e) => {
              setRecruiter(e.target.value);
              setPage(1);
            }}
            aria-label="Recruiter"
          >
            <option value="">Recruiter</option>
            {recruiterOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <FiChevronDown />
        </div>

        <div className="rm-select-wrap">
          <select
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Stage"
          >
            <option value="">Stage</option>
            {RECRUITMENT_STAGES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
          <FiChevronDown />
        </div>

        <button type="button" className="rm-more-btn">
          More
          <FiChevronDown />
        </button>

        <div className="rm-select-wrap ra-sort">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort"
          >
            <option value="newest">Newest</option>
            <option value="name">Name</option>
            <option value="priority">Priority</option>
          </select>
          <FiChevronDown />
        </div>
      </div>

      <div className="ra-stage-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={stageTab === 'all'}
          className={`ra-stage-tab${stageTab === 'all' ? ' is-active' : ''}`}
          onClick={() => {
            overlay.setTab('all');
            setPage(1);
          }}
        >
          All
        </button>
        {RECRUITMENT_STAGES.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={stageTab === s.id}
            className={`ra-stage-tab${stageTab === s.id ? ' is-active' : ''}`}
            onClick={() => {
              overlay.setTab(s.id);
              setPage(1);
            }}
          >
            {s.title} ({stageCounts[s.id] || 0})
          </button>
        ))}
      </div>

      <div className="rm-table-wrap ra-table-scroll">
        <table className="rm-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Position Applied</th>
              <th>Branch</th>
              <th>Experience</th>
              <th>Phone</th>
              <th>Applied Date</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Recruiter</th>
              <th>Resume</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="ra-empty">
                  Loading applicants…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan="11" className="ra-empty">
                  No applicants yet. Click Add Applicant to create one.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="rm-applicant-cell">
                      <div
                        className="rm-avatar"
                        style={{ background: row.avatar }}
                      >
                        {applicantInitials(row.name)}
                      </div>
                      <div>
                        <p className="rm-applicant-name">{row.name}</p>
                        <p className="rm-applicant-id">{row.appId}</p>
                      </div>
                    </div>
                  </td>
                  <td>{row.role}</td>
                  <td>{row.branch || '—'}</td>
                  <td>{row.experience}</td>
                  <td>{row.phone || '—'}</td>
                  <td>{row.appliedDate}</td>
                  <td>
                    <span className={`rm-stage-pill rm-stage-pill--${row.stage}`}>
                      {stageTitle(row.stage)}
                    </span>
                  </td>
                  <td>
                    <span className={`rm-priority rm-priority--${row.priority}`}>
                      {priorityLabel(row.priority)}
                    </span>
                  </td>
                  <td>{row.recruiter || '—'}</td>
                  <td>
                    {row.resumeFileUrl ? (
                      <a
                        className="ra-resume-link"
                        href={row.resumeFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        title={row.resumeFileName || 'Resume'}
                      >
                        <FiPaperclip /> View
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <div className="rm-row-actions">
                      <button type="button" className="rm-icon-btn" title="View">
                        <FiEye />
                      </button>
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="Schedule interview"
                        onClick={() =>
                          navigate({
                            pathname: '/',
                            search: `?page=recruitment-interviews&schedule=${encodeURIComponent(row.id)}`,
                          })
                        }
                      >
                        <FiCalendar />
                      </button>
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="Delete"
                        onClick={() => void handleDelete(row.id)}
                      >
                        <FiX />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className="ra-footer">
        <span>
          Showing {pageRows.length} of {filtered.length} applicants
        </span>
        <div className="ra-pagination">
          <button
            type="button"
            className="ra-page-btn"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <FiChevronLeft />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={`ra-page-btn${n === currentPage ? ' is-active' : ''}`}
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="ra-page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <FiChevronRight />
          </button>
        </div>
      </footer>

      {showAdd ? (
        <AddApplicant
          onClose={() => overlay.closeOverlay()}
          onCreated={() => void fetchApplicants()}
        />
      ) : null}
    </div>
  );
}
